
import React, { useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { VisitorsFilters } from "./VisitorsFilterBar";
import { Slider } from "@/components/ui/slider";

interface VisitorsFilterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (filters: VisitorsFilters) => void;
  onReset: () => void;
  filters: VisitorsFilters;
  industries: string[];
  companySizes: string[];
  locations: string[];
  personRoles: string[];
  seniorityLevels: string[];
  revenueMin?: number;
  revenueMax?: number;
}

const formatMM = (val: number) => {
  if (val >= 1000) {
    // Show 1 decimal if needed, otherwise just 1B, 2B, etc.
    const billions = val / 1000;
    return `$${billions % 1 === 0 ? billions.toFixed(0) : billions.toFixed(1)}B`;
  }
  return `$${val}M`;
};

const VisitorsFilterModal: React.FC<VisitorsFilterModalProps> = ({
  open, onOpenChange, onApply, onReset,
  filters, industries, companySizes, locations, personRoles, seniorityLevels, revenueMin, revenueMax,
}) => {
  const [draft, setDraft] = useState<VisitorsFilters>(filters);

  React.useEffect(() => {
    if(open) setDraft(filters);
  }, [open, filters]);

  const setFilter = (key: keyof VisitorsFilters, value: string | number) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  };

  const [sliderValue, setSliderValue] = useState<[number, number]>([
    draft.revenueMin ?? revenueMin ?? 1,
    draft.revenueMax ?? revenueMax ?? 10000,
  ]);

  React.useEffect(() => {
    setSliderValue([
      draft.revenueMin ?? revenueMin ?? 1,
      draft.revenueMax ?? revenueMax ?? 10000,
    ]);
  }, [draft.revenueMin, draft.revenueMax, revenueMin, revenueMax, open]);

  const handleSliderChange = (value: number[]) => {
    setSliderValue([value[0], value[1]]);
    setDraft(prev => ({
      ...prev,
      revenueMin: value[0],
      revenueMax: value[1],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0">
        <div className="px-6 py-6">
          <DialogHeader>
            <DialogTitle className="text-lg mb-2 flex items-center">
              <span className="mr-2">Filter Company Leads</span>
            </DialogTitle>
          </DialogHeader>
          <div>
            <p className="text-gray-900 mb-2 font-semibold text-base">Company Info</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Industry</label>
                <Select value={draft.industry} onValueChange={v => setFilter("industry", v)}>
                  <SelectTrigger className="bg-white border-gray-300">
                    <SelectValue placeholder="All Industries" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200 shadow-lg">
                    <SelectItem value="all" className="hover:bg-gray-100">All</SelectItem>
                    {industries.map(i => (
                      <SelectItem key={i} value={i} className="hover:bg-gray-100">{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Company Size</label>
                <div className="flex flex-wrap gap-2">
                  {companySizes.map(size => (
                    <Badge
                      key={size}
                      variant={draft.companySize === size ? "default" : "outline"}
                      className={`cursor-pointer ${draft.companySize === size
                        ? "bg-[#9b87f5] text-white border-[#9b87f5]" : "border-gray-200"}`}
                      onClick={() => setFilter("companySize", size)}
                    >
                      {size}
                    </Badge>
                  ))}
                  <Badge
                    variant={draft.companySize === "all" ? "default" : "outline"}
                    className={`cursor-pointer ${draft.companySize === "all"
                      ? "bg-[#9b87f5] text-white border-[#9b87f5]" : "border-gray-200"}`}
                    onClick={() => setFilter("companySize", "all")}
                  >
                    All
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Headquarters Location</label>
                <Select value={draft.location} onValueChange={v => setFilter("location", v)}>
                  <SelectTrigger className="bg-white border-gray-300">
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent 
                    className="bg-white border border-gray-200 shadow-lg max-h-[200px] overflow-y-auto" 
                    position="item-aligned"
                  >
                    <SelectItem value="all" className="hover:bg-gray-100">All</SelectItem>
                    {locations.map(location => (
                      <SelectItem key={location} value={location} className="hover:bg-gray-100">{location}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Revenue</label>
                <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-2">
                  <div className="flex justify-between mb-2">
                    <span className="text-xs font-medium">{formatMM(sliderValue[0])}</span>
                    <span className="text-xs font-medium">{formatMM(sliderValue[1])}</span>
                  </div>
                  <Slider
                    min={revenueMin ?? 1}
                    max={revenueMax ?? 10000}
                    step={1}
                    value={sliderValue}
                    onValueChange={handleSliderChange}
                    minStepsBetweenThumbs={1}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8">
            <p className="text-gray-900 mb-2 font-semibold text-base">Contact Details</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Job Title (Role)</label>
                <Select value={draft.personRole} onValueChange={v => setFilter("personRole", v)}>
                  <SelectTrigger className="bg-white border-gray-300">
                    <SelectValue placeholder="All Roles" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200 shadow-lg">
                    <SelectItem value="all" className="hover:bg-gray-100">All</SelectItem>
                    {personRoles.map(role => (
                      <SelectItem key={role} value={role} className="hover:bg-gray-100">{role}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Seniority Level</label>
                <div className="flex flex-wrap gap-2">
                  {seniorityLevels.map(level => (
                    <Badge
                      key={level}
                      variant={draft.seniorityLevel === level ? "default" : "outline"}
                      className={`cursor-pointer ${draft.seniorityLevel === level
                        ? "bg-[#9b87f5] text-white border-[#9b87f5]" : "border-gray-200"}`}
                      onClick={() => setFilter("seniorityLevel", level)}
                    >
                      {level}
                    </Badge>
                  ))}
                  <Badge
                    variant={draft.seniorityLevel === "all" ? "default" : "outline"}
                    className={`cursor-pointer ${draft.seniorityLevel === "all"
                      ? "bg-[#9b87f5] text-white border-[#9b87f5]" : "border-gray-200"}`}
                    onClick={() => setFilter("seniorityLevel", "all")}
                  >
                    All
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="bg-gray-50 px-6 py-4 flex justify-end rounded-b-xl border-t mt-0">
          <Button variant="outline" className="mr-2" onClick={() => { onReset(); onOpenChange(false); }}>
            Reset
          </Button>
          <Button className="bg-[#9b87f5] text-white" onClick={() => { onApply(draft); onOpenChange(false); }}>
            Apply Filters
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VisitorsFilterModal;
